import {ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  AangifteBerekening,
  BokkzAlgDataService,
  BokkzClient,
  BokkzIbDataService,
  FeatureServiceComponent,
  IncomeTaxEntity,
  MenuInterface,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {ContextMenuConfiguration} from '@snelstart/ui-context-menu';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule, NgStyle} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-income-list',
  templateUrl: './feature-tax-income-list.component.html',
  imports: [SharedUiTableModule, SharedUiButtonModule, NgStyle, CommonModule, FeatureServiceComponent],
  styleUrls: ['./feature-tax-income-list.component.scss'],
})
export class FeatureTaxIncomeListComponent implements OnInit {

  @Input() public id!: string;
  @Output() public details = new EventEmitter<string>();
  protected tableConfig!: TableConfiguration<any, any>;
  private lastTableStateKey: string | null = null;
  private readonly options$ = new BehaviorSubject<MenuInterface[]>([]);
  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzIbDataService);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public config = ContextMenuConfiguration.create({
    icon: 'ellipsis-vertical',
    iconType: 'light',
    definition: (menu) => menu.createGroup('Reminders').and.createGroup(),
  });

  public clientId: any;
  public showTable = false;
  public year = 0;
  public options: MenuInterface[] = [];

  public sumOverigInkomen = 0;
  public sumOverigInkomenPartner = 0;
  public sumEigenWoningAangever = 0;
  public sumEigenWoningPartner = 0;
  public sumPremiesInkomensvoorzieningen = 0;
  public sumPremiesInkomensvoorzieningenPartner = 0;
  public sumBox2Aangever = 0;
  public sumBox2Partner = 0;
  public sumPGAAangever = 0;
  public sumPGAPartner = 0;
  public sumVAIB: number | undefined = 0;
  public sumVAZVW: number | undefined = 0;
  public sumDivBel: number | undefined = 0;
  public sumVAIBPartner: number | undefined = 0;
  public sumVAZVWPartner: number | undefined = 0;
  public sumDivBelPartner: number | undefined = 0;
  public sumVoorheffingen = 0;
  public sumVoorheffingenPartner = 0;
  public sumAanslagenIbenZvw = 0;
  public sumIB: number | undefined = 0;
  public sumZVW: number | undefined = 0;
  public sumAanslagenIbenZvwPartner = 0;
  public sumIBPartner: number | undefined = 0;
  public sumZVWPartner: number | undefined = 0;
  public bedragRevisierente: number | undefined = 0;
  public bedragRevisierentePartner: number | undefined = 0;
  public dialogVisible = false;
  public activePage: null | string = '';

  readonly clientId$ = this.activatedRoute.params.pipe(
    map((params) => params['clientId'] ?? ''),
    filter((clientId) => !!clientId),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly year$ = this.algDataService.selectedYear.pipe(
    filter((year): year is number => year != null),
    distinctUntilChanged(),
    tap((year) => {
      this.year = year;
    }),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly incomeTaxLoad$ = combineLatest({
    clientId: this.clientId$,
    year: this.year$,
    reload: this.reload$,
  }).pipe(
    switchMap(({clientId, year}) =>
      this.bokkzClient.getIncomeTax(clientId, String(year)).pipe(
        tap((incomeTax) => {
          this.clientId = clientId;
          this.dataService.updateIncomeTaxEntity(incomeTax);
        }),
        catchError(() => of(null)),
      ),
    ),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly incomeTaxEntity$ = merge(this.incomeTaxLoad$, this.dataService.incomeTaxEntity).pipe(
    filter((incomeTax): incomeTax is IncomeTaxEntity => !!incomeTax?.sstRelationId || !!incomeTax?.aangifte),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly aangifteBerekening$: Observable<AangifteBerekening | null> = this.dataService.aangifteBerekening$.pipe(
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly tableVm$ = this.incomeTaxLoad$.pipe(
    switchMap((incomeTaxEntity) => {
      if (!incomeTaxEntity?.aangifte) {
        this.resetSums();
        this.dataService.updateAangifteBerekening(null as any);
        this.dataService.aantalValidaties.next(0);
        this.dataService.menuInterface.next([]);
        this.options$.next([]);

        return of({
          incomeTaxEntity: null,
          options: [] as MenuInterface[],
          tableConfig: this.createTableConfig([]),
        });
      }

      this.dataService.createMenuInterface(incomeTaxEntity);
      const options = this.dataService.menuInterface.value ?? [];
      this.options$.next([...options]);

      return this.dataService.calculateLatestIncomeTaxReturn().pipe(
        tap((berekening) => this.dataService.updateAangifteBerekening(berekening)),
        map(() => ({
          incomeTaxEntity,
          options,
          tableConfig: this.createTableConfig(options),
        })),
        startWith({
          incomeTaxEntity,
          options,
          tableConfig: this.createTableConfig(options),
        }),
      );
    }),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  ngOnInit(): void {
    this.aangifteBerekening$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((aangifteBerekening) => {
      if (aangifteBerekening) {
        this.getSumOverigeInkomsten(aangifteBerekening);
        this.getSumOverigeInkomstenPartner(aangifteBerekening);
        this.getEigenWoningAangever(aangifteBerekening);
        this.getEigenWoningPartner(aangifteBerekening);
        this.getSumPremiesInkomensvoorzieningen(aangifteBerekening);
        this.getSumPremiesInkomensvoorzieningenPartner(aangifteBerekening);
        this.getBox2Aangever(aangifteBerekening);
        this.getBox2Partner(aangifteBerekening);
        this.getSumPGAAangever(undefined, aangifteBerekening);
        this.getSumAanslagenIbenZvw(aangifteBerekening);
        this.getSumAanslagenIbenZvwPartner(aangifteBerekening);
      }
    });

    this.dataService.incomeTaxEntity.pipe(debounceTime(0),
      filter((incomeTaxEntity): incomeTaxEntity is IncomeTaxEntity => !!incomeTaxEntity?.aangifte),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((incomeTaxEntity) => {
      this.dataService.createMenuInterface(incomeTaxEntity);
    });

    this.tableVm$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vm) => {
      this.options = vm.options;

      const tableStateKey = vm.incomeTaxEntity?.id != null ? `data-${vm.incomeTaxEntity.id}-${this.year}` : `empty-${this.year}`;

      if (this.lastTableStateKey !== tableStateKey) {
        this.lastTableStateKey = tableStateKey;

        this.showTable = false;
        this.tableConfig = undefined as any;
        this.cdr.detectChanges();

        this.tableConfig = vm.tableConfig;
        this.showTable = true;
        this.cdr.detectChanges();
      } else {
        this.tableConfig = vm.tableConfig;
        this.showTable = true;
      }

      if (vm.incomeTaxEntity) {
        this.getSumPGAAangever(vm.incomeTaxEntity, undefined);
        this.getSumPGAPartner(vm.incomeTaxEntity);
        this.getRevisieRenteAangever(vm.incomeTaxEntity);
        this.getRevisieRentePartner(vm.incomeTaxEntity);
        this.getSumVoorheffingen(vm.incomeTaxEntity);
        this.getSumVoorheffingenPartner(vm.incomeTaxEntity);
      }
    });

    this.dataService.menuInterface.pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef)).subscribe((menuInterface) => {
      const old = this.options$.value;
      const next = [...(menuInterface ?? [])];

      if (JSON.stringify(old) === JSON.stringify(next)) {
        return;
      }

      this.options$.next(next);
      this.options = next;
      this.tableConfig = this.createTableConfig(next);
      this.cdr.detectChanges();

      for (let i = 0; i < next.length; i++) {
        if (JSON.stringify(old[i]) !== JSON.stringify(next[i])) {
          dispatchSavedItemEvent({
            identifier: next[i].value,
            result: next[i] as unknown as Record<number, MenuInterface>,
          });

          if (!next[i].visible) {
            dispatchDeletedItemEvent({
              identifier: next[i].value,
            });
          }
        }
      }

      this.cdr.detectChanges();
    });
  }

  public onAangifteCreated(): void {
    this.reload$.next();
  }

  private createTableConfig(options: MenuInterface[]): TableConfiguration<any, any> {
    const criteria = new SearchCriteria();

    return TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria,

      search: () => {
        const sortedGroups = options
          .filter((item) => item.visible)
          .map((group) => ({
            ...group,
            subonderdelen: (group.subonderdelen || []).filter((sub) => sub.visible).sort((a, b) => a.value - b.value),
          }))
          .sort((a, b) => a.value - b.value);

        return of(new SearchResult(sortedGroups, sortedGroups.length));
      },
      details: (entity) => {
        if (entity) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024 ? 'Module niet beschikbaar' : $localize`Geen aangifte gevonden voor ` + this.year,
        subText:
          this.year < 2024
            ? 'De aangifte inkomstenbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
            : 'Maak een nieuwe aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe aangifte`,
        buttonConfig:
          this.year < 2024
            ? undefined
            : ButtonConfiguration.create({
              onClick: () =>
                new Observable((subscriber) => {
                  this.dialogVisible = true;
                  subscriber.next(true);
                  subscriber.complete();
                }),
            }),
      }),
    });
  }

  public navigateToSubPage(pageMapping: string): void {
    let url = '/tax-income/' + 'modules/' + this.clientId + '/' + pageMapping;
    this.router.navigate([url]).then(() => console.log('complete'));
    this.activePage = pageMapping;
  }

  private getSumOverigeInkomsten(berekening: AangifteBerekening) {
    let sumRow = 0;
    let sumPu = 0;
    if (berekening.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden !== undefined) {
      sumRow = berekening.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden;
    }
    if (berekening.box1?.opbouwInkomen?.periodiekeUitkeringen !== undefined) {
      sumPu = berekening.box1?.opbouwInkomen?.periodiekeUitkeringen;
    }
    this.sumOverigInkomen = sumPu + sumRow;
  }

  private getSumOverigeInkomstenPartner(berekening: AangifteBerekening) {
    let sumRow = 0;
    let sumPu = 0;
    if (berekening.partnerBerekening?.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden !== undefined) {
      sumRow = berekening.partnerBerekening?.box1?.opbouwInkomen?.resultaatUitOverigeWerkzaamheden;
    }
    if (berekening.partnerBerekening?.box1?.opbouwInkomen?.periodiekeUitkeringen !== undefined) {
      sumPu = berekening.partnerBerekening?.box1?.opbouwInkomen?.periodiekeUitkeringen;
    }
    this.sumOverigInkomenPartner = sumPu + sumRow;
  }

  private getEigenWoningAangever(berekening: AangifteBerekening) {
    let saldoEigenWoning: number | undefined = 0;
    let aftrekGOGEW: number | undefined = 0;
    saldoEigenWoning = berekening.box1?.opbouwInkomen?.inkomstenUitEigenWoning;
    aftrekGOGEW = berekening.box1?.opbouwInkomen?.aftrekWegensGeenOfGeringeEigenWoningSchuld;
    this.sumEigenWoningAangever = (saldoEigenWoning != undefined ? saldoEigenWoning : 0) - (aftrekGOGEW != undefined ? aftrekGOGEW : 0);
  }

  private getEigenWoningPartner(berekening: AangifteBerekening) {
    let saldoEigenWoning: number | undefined = 0;
    let aftrekGOGEW: number | undefined = 0;
    saldoEigenWoning = berekening.partnerBerekening?.box1?.opbouwInkomen?.inkomstenUitEigenWoning;
    aftrekGOGEW = berekening.partnerBerekening?.box1?.opbouwInkomen?.aftrekWegensGeenOfGeringeEigenWoningSchuld;
    this.sumEigenWoningPartner = (saldoEigenWoning != undefined ? saldoEigenWoning : 0) - (aftrekGOGEW != undefined ? aftrekGOGEW : 0);
  }

  private getSumPremiesInkomensvoorzieningen(berekening: AangifteBerekening) {
    let sumUitgInkVz = 0;
    let sumNegInkVz = 0;
    sumUitgInkVz = berekening.box1?.opbouwInkomen?.uitgavenVoorInkomensvoorzieningen ?? 0;
    sumNegInkVz = berekening.box1?.opbouwInkomen?.negatieveUitgavenVoorInkomensvoorzieningen ?? 0;
    this.sumPremiesInkomensvoorzieningen = sumUitgInkVz - sumNegInkVz;
  }

  private getSumPremiesInkomensvoorzieningenPartner(berekening: AangifteBerekening) {
    let sumUitgInkVz = 0;
    let sumNegInkVz = 0;
    sumUitgInkVz = berekening.partnerBerekening?.box1?.opbouwInkomen?.uitgavenVoorInkomensvoorzieningen ?? 0;
    sumNegInkVz = berekening.partnerBerekening?.box1?.opbouwInkomen?.negatieveUitgavenVoorInkomensvoorzieningen ?? 0;
    this.sumPremiesInkomensvoorzieningenPartner = sumUitgInkVz - sumNegInkVz;
  }

  private getBox2Aangever(berekening: AangifteBerekening) {
    let saldobox2: number | undefined = 0;
    saldobox2 = berekening.box2?.opbouwInkomen?.inkomenUitAanmerkelijkBelang ?? 0;
    this.sumBox2Aangever = saldobox2 != undefined ? saldobox2 : 0;
  }

  private getBox2Partner(berekening: AangifteBerekening) {
    let saldobox2: number | undefined = 0;
    saldobox2 = berekening.partnerBerekening?.box2?.opbouwInkomen?.inkomenUitAanmerkelijkBelang ?? 0;
    this.sumBox2Partner = saldobox2 != undefined ? saldobox2 : 0;
  }

  private getSumPGAAangever(incomeTaxEntity?: IncomeTaxEntity, berekening?: AangifteBerekening) {
    if (incomeTaxEntity?.aangifte?.verdeling?.aandeelPGA) {
      this.sumPGAAangever = incomeTaxEntity.aangifte.verdeling.aandeelPGA;
    } else if (berekening) {
      const sumZorgkosten = berekening.opbouwPGA?.specifiekeZorgkosten ? berekening.opbouwPGA?.specifiekeZorgkosten : 0;
      const sumGiften = berekening.opbouwPGA?.aftrekbareGiften ? berekening.opbouwPGA?.aftrekbareGiften : 0;
      const sumScholing = berekening.opbouwPGA?.scholingsuitgaven ? berekening.opbouwPGA?.scholingsuitgaven : 0;
      const sumWeekend = berekening.opbouwPGA?.weekenduitgaven ? berekening.opbouwPGA?.weekenduitgaven : 0;
      const sumOnderhoud = berekening.opbouwPGA?.onderhoudsverplichtingen ? berekening.opbouwPGA?.onderhoudsverplichtingen : 0;
      const sumRestant = berekening.opbouwPGA?.restantPgaVoorgaandeJaren ? berekening.opbouwPGA?.restantPgaVoorgaandeJaren : 0;
      this.sumPGAAangever = sumZorgkosten + sumGiften + sumScholing + sumWeekend + sumOnderhoud + sumRestant;
    }
  }

  private getSumPGAPartner(incomeTaxEntity: IncomeTaxEntity) {
    this.sumPGAPartner = incomeTaxEntity.aangifte?.partnerAangifte?.verdeling?.aandeelPGA ?? 0;
  }

  private getSumVoorheffingen(incomeTaxEntity: IncomeTaxEntity) {
    this.sumVAIB = incomeTaxEntity.aangifte?.voorheffingen?.voorlopigeAanslag ?? 0;
    this.sumVAZVW = incomeTaxEntity.aangifte?.voorheffingen?.voorlopigeAanslagZvw ?? 0;
    this.sumDivBel = incomeTaxEntity.aangifte?.verdeling?.aandeelDividendbelasting ?? 0;
    this.sumVoorheffingen =
      (this.sumVAIB ? this.sumVAIB : 0) + (this.sumVAZVW ? this.sumVAZVW : 0) + (this.sumDivBel ? this.sumDivBel : 0);
  }

  private getSumVoorheffingenPartner(incomeTaxEntity: IncomeTaxEntity) {
    this.sumVAIBPartner = incomeTaxEntity.aangifte?.partnerAangifte?.voorheffingen?.voorlopigeAanslag ?? 0;
    this.sumVAZVWPartner = incomeTaxEntity.aangifte?.partnerAangifte?.voorheffingen?.voorlopigeAanslagZvw ?? 0;
    this.sumDivBelPartner = incomeTaxEntity.aangifte?.partnerAangifte?.verdeling?.aandeelDividendbelasting ?? 0;

    this.sumVoorheffingenPartner =
      (this.sumVAIBPartner ? this.sumVAIBPartner : 0) +
      (this.sumVAZVWPartner ? this.sumVAZVWPartner : 0) +
      (this.sumDivBelPartner ? this.sumDivBelPartner : 0);
  }

  private getRevisieRenteAangever(incomeTaxEntity: IncomeTaxEntity) {
    this.bedragRevisierente = incomeTaxEntity.aangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente ?? 0;
  }

  private getRevisieRentePartner(incomeTaxEntity: IncomeTaxEntity) {
    this.bedragRevisierentePartner =
      incomeTaxEntity.aangifte?.partnerAangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente ?? 0;
  }

  private getSumAanslagenIbenZvw(berekening: AangifteBerekening) {
    this.sumIB = berekening.verschuldigdeInkomstenbelasting ?? 0;
    this.sumZVW = berekening.verschuldigdeZVW ?? 0;
    this.sumAanslagenIbenZvw =
      (this.sumIB ? this.sumIB : 0) + (this.sumZVW ? this.sumZVW : 0) + (this.bedragRevisierente ? this.bedragRevisierente : 0);
  }

  private getSumAanslagenIbenZvwPartner(berekening: AangifteBerekening) {
    this.sumIBPartner = berekening.partnerBerekening?.verschuldigdeInkomstenbelasting ?? 0;
    this.sumZVWPartner = berekening.partnerBerekening?.verschuldigdeZVW ?? 0;
    this.sumAanslagenIbenZvwPartner =
      (this.sumIBPartner ? this.sumIBPartner : 0) +
      (this.sumZVWPartner ? this.sumZVWPartner : 0) +
      (this.bedragRevisierentePartner ? this.bedragRevisierentePartner : 0);
  }

  private resetSums(): void {
    this.sumOverigInkomen = 0;
    this.sumOverigInkomenPartner = 0;
    this.sumEigenWoningAangever = 0;
    this.sumEigenWoningPartner = 0;
    this.sumPremiesInkomensvoorzieningen = 0;
    this.sumPremiesInkomensvoorzieningenPartner = 0;
    this.sumBox2Aangever = 0;
    this.sumBox2Partner = 0;
    this.sumPGAAangever = 0;
    this.sumPGAPartner = 0;
    this.sumVoorheffingen = 0;
    this.sumVoorheffingenPartner = 0;
    this.sumAanslagenIbenZvw = 0;
    this.sumAanslagenIbenZvwPartner = 0;
  }
}
